import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import '../../assets/css/case-search.css';
import { getPropertiesByState, getStateName, type StateIndex } from './propertyUtils';

type SuggestionType = 'state' | 'city' | 'community';

interface Suggestion {
  label: string;
  type: SuggestionType;
  url: string;
}

interface SearchAutocompleteProps {
  placeholder?: string;
  variant?: 'hero' | 'inline';
  onNavigate?: (url: string) => void;
  className?: string;
  baseUrl?: string;
  /** Text for the submit button (hero variant only) */
  buttonText?: string;
}

const TYPE_LABELS: Record<SuggestionType, string> = {
  state: 'States',
  city: 'Cities',
  community: 'Communities',
};

const TYPE_ORDER: SuggestionType[] = ['state', 'city', 'community'];

function isValidStateAbbr(abbr: string): boolean {
  return /^[A-Z]{2}$/.test(abbr);
}

function buildSuggestions(index: StateIndex): Suggestion[] {
  const suggestions: Suggestion[] = [];

  for (const stateSlug of Object.keys(index)) {
    const state = index[stateSlug];

    // Skip entries created from bad data (full state/city names used as slugs)
    if (!isValidStateAbbr(state.abbr)) continue;

    suggestions.push({
      label: state.name,
      type: 'state',
      url: `/apartments/${stateSlug}/`,
    });

    for (const citySlug of Object.keys(state.cities)) {
      const city = state.cities[citySlug];
      suggestions.push({
        label: `${city.name}, ${state.abbr}`,
        type: 'city',
        url: `/apartments/${stateSlug}/${citySlug}/`,
      });

      for (const prop of city.properties) {
        const slug = prop._slug || prop.property_slug;
        if (!slug) continue;
        suggestions.push({
          label: prop.property_name,
          type: 'community',
          url: `/apartments/${stateSlug}/${citySlug}/${slug}/`,
        });
      }
    }
  }

  return suggestions;
}

function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <strong>{text.slice(idx, idx + query.length)}</strong>
      {text.slice(idx + query.length)}
    </>
  );
}

function vibesitesNavigate(url: string) {
  const nav = (window as any).__vibesites_navigate;
  if (nav) {
    nav(url);
    return;
  }
  // Simulate a link click so document-level click handlers
  // (SPA router or editor frame handler) can intercept it.
  const a = document.createElement('a');
  a.href = url;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function SearchAutocomplete({
  placeholder = 'Search by Community, City, State or Zip',
  variant = 'hero',
  onNavigate,
  className,
  baseUrl = '/apartments',
  buttonText = 'Find a home',
}: SearchAutocompleteProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const index = getPropertiesByState();
  const allSuggestions = useMemo(() => buildSuggestions(index), [index]);

  const filtered = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.trim().toLowerCase();
    const regex = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');

    const matches = allSuggestions.filter(s => regex.test(s.label));

    // Deduplicate by label+type
    const seen = new Set<string>();
    const deduped: Suggestion[] = [];
    for (const m of matches) {
      const key = `${m.type}:${m.label}`;
      if (!seen.has(key)) {
        seen.add(key);
        deduped.push(m);
      }
    }

    // Group by type, respecting TYPE_ORDER, cap at 12 total
    const grouped: Suggestion[] = [];
    for (const type of TYPE_ORDER) {
      const ofType = deduped.filter(s => s.type === type);
      grouped.push(...ofType);
    }
    return grouped.slice(0, 12);
  }, [query, allSuggestions]);

  const navigate = useCallback((url: string) => {
    setIsOpen(false);
    setQuery('');
    if (onNavigate) {
      onNavigate(url);
    } else {
      vibesitesNavigate(url);
    }
  }, [onNavigate]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (activeIndex >= 0 && activeIndex < filtered.length) {
      navigate(filtered[activeIndex].url);
      return;
    }
    const q = query.trim();
    if (q) {
      navigate(`${baseUrl}/?search=${encodeURIComponent(q)}`);
    } else {
      navigate(`${baseUrl}/`);
    }
  }, [activeIndex, filtered, query, navigate, baseUrl]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen || filtered.length === 0) {
      if (e.key === 'ArrowDown' && query.trim() && filtered.length > 0) {
        setIsOpen(true);
        setActiveIndex(0);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(prev => (prev + 1) % filtered.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(prev => (prev <= 0 ? filtered.length - 1 : prev - 1));
        break;
      case 'Escape':
        setIsOpen(false);
        setActiveIndex(-1);
        break;
      case 'Enter':
        if (activeIndex >= 0 && activeIndex < filtered.length) {
          e.preventDefault();
          navigate(filtered[activeIndex].url);
        }
        break;
    }
  }, [isOpen, filtered, activeIndex, navigate, query]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    setActiveIndex(-1);
    setIsOpen(val.trim().length > 0);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll('.case-search-item');
    items[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  // Group filtered results for rendering with category headers
  const groupedForRender = useMemo(() => {
    const groups: { type: SuggestionType; items: { suggestion: Suggestion; globalIndex: number }[] }[] = [];
    let currentType: SuggestionType | null = null;

    filtered.forEach((s, i) => {
      if (s.type !== currentType) {
        currentType = s.type;
        groups.push({ type: s.type, items: [] });
      }
      groups[groups.length - 1].items.push({ suggestion: s, globalIndex: i });
    });

    return groups;
  }, [filtered]);

  const showDropdown = isOpen && filtered.length > 0;

  return (
    <div
      ref={containerRef}
      className={`case-search-autocomplete case-search-autocomplete--${variant}${className ? ` ${className}` : ''}`}
    >
      <form className="case-search-form" onSubmit={handleSubmit} autoComplete="off">
        <div className="case-search-input-wrap">
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => { if (query.trim() && filtered.length > 0) setIsOpen(true); }}
            placeholder={placeholder}
            aria-label={placeholder}
            aria-expanded={showDropdown}
            aria-autocomplete="list"
            aria-controls="case-search-listbox"
            role="combobox"
            className="case-search-input"
          />
          {variant === 'hero' && (
            <button type="submit" className="case-search-btn">{buttonText}</button>
          )}
          {variant === 'inline' && (
            <button type="submit" className="case-search-btn-icon" aria-label="Search">
              <svg viewBox="0 0 512 512" width="16" height="16" fill="currentColor">
                <path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0s208 93.1 208 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z" />
              </svg>
            </button>
          )}
        </div>

        {showDropdown && (
          <div
            ref={listRef}
            id="case-search-listbox"
            role="listbox"
            className="case-search-dropdown"
          >
            {groupedForRender.map(group => (
              <div key={group.type} className="case-search-group">
                <div className="case-search-group-label">{TYPE_LABELS[group.type]}</div>
                {group.items.map(({ suggestion, globalIndex }) => (
                  <div
                    key={suggestion.url}
                    role="option"
                    aria-selected={globalIndex === activeIndex}
                    className={`case-search-item${globalIndex === activeIndex ? ' active' : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      navigate(suggestion.url);
                    }}
                    onMouseEnter={() => setActiveIndex(globalIndex)}
                  >
                    {highlightMatch(suggestion.label, query.trim())}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </form>
    </div>
  );
}

export default SearchAutocomplete;
